/**
 * Builder adapters for the shared <AgentBuilder scope> component.
 *
 * Phase 2 of PR-A AgentBuilder shared-component refactor (2026-05-11).
 * Each adapter implements the BuilderAdapter interface — its job is to
 * smooth over the scope-conditional catalog hooks, save payloads, and
 * affordance gating so the shared component body sees ONE normalized
 * shape regardless of scope.
 *
 * See AgentBuilder.types.ts for the full scope-deviation table.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  AgentAutonomy,
  AgentTrigger,
  AgentCategory,
} from '@/lib/mock-agents-data';
import { apiClient } from '@/lib/api-client';
import { useLibraryFolders } from '@/lib/use-library-folders';
import { useSysLibFolders } from '@/lib/use-sysadmin-library';

// Tenant catalog hooks
import {
  useWorkspaceTools,
  type WorkspaceToolListRow,
} from '@/lib/use-workspace-tools';
import {
  useStudioSkills,
  type StudioSkill,
} from '@/lib/use-studio-skills';
import { useCreateAgent } from '@/lib/use-agents';
import {
  useTenantArgusModels,
  type TenantBrand,
} from '@/lib/use-tenant-argus-models';

// Sysadmin catalog hooks
import {
  useArgusModels,
  useWrapperFunctions,
  type ArgusModelSummary,
  type WrapperFunction,
} from '@/lib/use-argus-models';
import {
  useSysadminTools,
  type SysadminToolListItem,
} from '@/lib/use-sysadmin-tools';
import {
  useCatalogSkills,
  type CatalogSkill,
} from '@/lib/use-sysadmin-catalog-skills';
import {
  useCatalogWorkflows,
  type CatalogWorkflow,
} from '@/lib/use-sysadmin-catalog-workflows';

// Tenant-scoped system-catalog hooks (Studio R1 fix — PR #459 audit).
// Tenant adapter MUST NOT call the sysadmin /admin/platform/* hooks
// above; those return 401 for non-sysadmin users. The /functions/system
// + /workflows/system endpoints serve the same data in I-M6.7 redacted
// shape and authorize tenants of any role.
import {
  useTenantSystemFunctions,
  useTenantSystemWorkflows,
} from '@/lib/use-tenant-system-catalog';
import {
  useCreateCatalogAgent,
  useUpdateCatalogAgent,
  useCatalogAgent,
  type CatalogAgentDetail,
} from '@/lib/use-sysadmin-catalog-agents';

import {
  slugify,
  applySetModelMutation,
  mapAutonomyToApi,
  mapAutonomyFromApi,
  modesToTriggers,
  triggersToModes,
} from './AgentBuilder.helpers';
import type {
  BuilderAdapter,
  BuilderConfig,
  NormalizedLibraryRef,
  NormalizedExistingAgent,
  NormalizedFunction,
  NormalizedModel,
  NormalizedSkill,
  NormalizedTool,
  NormalizedWorkflow,
  SaveAgentArgs,
} from './AgentBuilder.types';

export type BuilderLibraryRefKind = NormalizedLibraryRef['kind'];

export interface BuilderLibraryRef {
  kind: BuilderLibraryRefKind;
  id: string;
}

export interface BuilderKnowledgeScope {
  libraryRefs: BuilderLibraryRef[];
  knowledgeSources?: string[];
  [key: string]: unknown;
}

const LIBRARY_REF_KINDS: BuilderLibraryRefKind[] = [
  'folder',
  'article',
  'tag',
  'document',
  'document_folder',
  'source',
];

const LIBRARY_REF_KIND_SET = new Set<string>(LIBRARY_REF_KINDS);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function encodeLibraryRef(ref: BuilderLibraryRef): string {
  return `${ref.kind}:${ref.id}`;
}

export function decodeLibraryRef(value: string): BuilderLibraryRef | null {
  const [kind, id, ...rest] = value.split(':');
  if (rest.length > 0) return null;
  if (!LIBRARY_REF_KIND_SET.has(kind)) return null;
  if (!UUID_PATTERN.test(id ?? '')) return null;
  return { kind: kind as BuilderLibraryRefKind, id };
}

export function knowledgeSourcesToLibraryRefs(values: readonly string[]): BuilderLibraryRef[] {
  const refs: BuilderLibraryRef[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const ref = decodeLibraryRef(value);
    if (!ref) continue;
    const key = encodeLibraryRef(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(ref);
  }
  return refs;
}

/**
 * The picker values for an agent's saved Library refs. Since decision 18 a
 * 'folder' ref and a 'document_folder' ref name the SAME folder store (the
 * API resolves both from it, catalog-agents.ts), and the picker offers each
 * folder once, as 'folder' — so a ref saved as 'document_folder' is shown,
 * and saved again, as the 'folder' ref of the same id, and a folder saved
 * under both kinds is selected once.
 */
export function libraryRefsToPickerValues(refs: readonly BuilderLibraryRef[]): string[] {
  const values: string[] = [];
  const seen = new Set<string>();
  for (const ref of refs) {
    const kind: BuilderLibraryRefKind = ref.kind === 'document_folder' ? 'folder' : ref.kind;
    const value = encodeLibraryRef({ kind, id: ref.id });
    if (seen.has(value)) continue;
    seen.add(value);
    values.push(value);
  }
  return values;
}

function legacyKnowledgeSources(values: readonly string[]): string[] {
  return values.filter((value) => !decodeLibraryRef(value));
}

type TenantWikiArticle = { id: string; title: string; status?: string; excerpt?: string | null };
type TenantWikiTag = { id: string; name: string };
type TenantDocument = { id: string; title: string; status?: string; fileName?: string; description?: string | null };

type SysadminArticle = { id: string; title: string; status?: string };
type SysadminTag = { id: string; name: string; status?: string };
type SysadminDocument = { id: string; title: string; publishStatus?: string; fileName?: string };
type SysadminSource = { id: string; displayName: string; status?: string; connectorKind?: string };

function byKindThenName(a: NormalizedLibraryRef, b: NormalizedLibraryRef): number {
  return a.kind.localeCompare(b.kind) || a.displayName.localeCompare(b.displayName);
}

function refRow(
  kind: BuilderLibraryRefKind,
  id: string,
  displayName: string,
  description: string,
  status = 'available',
): NormalizedLibraryRef {
  return { kind, id, displayName, description, status };
}

// Decision 18 / LB2T3-BF-06 — the folder refs come from the ONE folder query
// of each scope (useLibraryFolders for a tenant, useSysLibFolders('published')
// for the System Library), observed directly, so a folder write's
// invalidation reaches this picker at once. Only the other refs (articles,
// tags, documents, sources) are fetched here, under their own key. Each folder
// is offered once, as a 'folder' ref, never again as a 'document_folder'.
async function fetchTenantOtherLibraryRefs(): Promise<NormalizedLibraryRef[]> {
  const [articles, tags, documents] = await Promise.all([
    apiClient.get<{ data: TenantWikiArticle[] }>('/wiki/articles?status=published&pageSize=100'),
    apiClient.get<{ data: TenantWikiTag[] }>('/wiki/tags'),
    apiClient.get<{ data: TenantDocument[] }>('/knowledge/documents?status=ready&pageSize=100'),
  ]);

  return [
    ...articles.data.map((a) =>
      refRow('article', a.id, a.title, a.excerpt ?? 'Published wiki article', a.status ?? 'published'),
    ),
    ...tags.data.map((t) => refRow('tag', t.id, t.name, 'Wiki tag')),
    ...documents.data.map((d) =>
      refRow('document', d.id, d.title, d.description ?? d.fileName ?? 'Uploaded document', d.status ?? 'ready'),
    ),
  ];
}

async function fetchSysadminOtherLibraryRefs(): Promise<NormalizedLibraryRef[]> {
  const [articles, tags, documents, sources] = await Promise.all([
    apiClient.get<{ items: SysadminArticle[] }>('/admin/platform/library/articles?status=published'),
    apiClient.get<{ items: SysadminTag[] }>('/admin/platform/library/tags?status=published'),
    apiClient.get<{ items: SysadminDocument[] }>('/admin/platform/library/documents?publishStatus=published'),
    apiClient.get<{ items: SysadminSource[] }>('/admin/platform/library/sources?status=published'),
  ]);

  return [
    ...articles.items.map((a) => refRow('article', a.id, a.title, 'System wiki article', a.status ?? 'published')),
    ...tags.items.map((t) => refRow('tag', t.id, t.name, 'System wiki tag', t.status ?? 'published')),
    ...documents.items.map((d) =>
      refRow('document', d.id, d.title, d.fileName ?? 'System document', d.publishStatus ?? 'published'),
    ),
    ...sources.items.map((s) =>
      refRow('source', s.id, s.displayName, s.connectorKind ?? 'System source', s.status ?? 'published'),
    ),
  ];
}

/** Exported for tests (AgentBuilder.library-refs.lb2t3.test.tsx). */
export function useBuilderLibraryRefs(scope: 'tenant' | 'sysadmin') {
  const tenantFoldersQ = useLibraryFolders({ enabled: scope === 'tenant' });
  const systemFoldersQ = useSysLibFolders('published', { enabled: scope === 'sysadmin' });
  const otherQ = useQuery<NormalizedLibraryRef[], Error>({
    queryKey: ['agent-builder', scope, 'library-refs'],
    queryFn: () => (scope === 'sysadmin' ? fetchSysadminOtherLibraryRefs() : fetchTenantOtherLibraryRefs()),
    staleTime: 30_000,
  });

  const folderRefs = useMemo<NormalizedLibraryRef[] | undefined>(() => {
    if (scope === 'sysadmin') {
      return systemFoldersQ.data?.items.map((f) =>
        refRow('folder', f.id, f.name, 'System Library folder', f.status ?? 'published'),
      );
    }
    return tenantFoldersQ.data?.map((f) => refRow('folder', f.id, f.name, f.description ?? 'Library folder'));
  }, [scope, systemFoldersQ.data, tenantFoldersQ.data]);

  // Nothing is offered until both halves are read: a half list would look complete.
  const data = useMemo<NormalizedLibraryRef[] | undefined>(
    () => (folderRefs && otherQ.data ? [...folderRefs, ...otherQ.data].sort(byKindThenName) : undefined),
    [folderRefs, otherQ.data],
  );
  const foldersQ = scope === 'sysadmin' ? systemFoldersQ : tenantFoldersQ;
  return {
    data,
    isLoading: foldersQ.isLoading || otherQ.isLoading,
    error: (foldersQ.error as Error | null) ?? otherQ.error ?? null,
  };
}

// ─── Pure save-payload serializers (exported for tests) ────────────────────
//
// Bucket C of the scope-deviation table — 12 field differences between
// tenant + sysadmin save bodies. These pure functions let unit tests
// assert payload shape without rendering hooks. Each adapter's
// `useSaveAgent().save()` calls the matching serializer below.

export interface TenantSavePayload {
  name: string;
  description: string | undefined;
  systemPrompt: string;
  icon: string;
  category: string;
  tools: string[];
  modelBrandIds: string[] | undefined;
  skills: string[];
  knowledgeScope: BuilderKnowledgeScope;
  triggerModes: AgentTrigger[];
  autonomyLevel: 'read_only' | 'approval_required' | 'auto_execute';
}

export function serializeTenantSavePayload(config: BuilderConfig): TenantSavePayload {
  return {
    name: config.name,
    description: config.description || undefined,
    systemPrompt: config.systemPrompt,
    icon: config.icon,
    category: config.category,
    tools: config.selectedTools,
    modelBrandIds: config.modelIds.length > 0 ? config.modelIds : undefined,
    skills: config.selectedSkills,
    knowledgeScope: {
      libraryRefs: knowledgeSourcesToLibraryRefs(config.knowledgeSources),
      ...(legacyKnowledgeSources(config.knowledgeSources).length > 0
        ? { knowledgeSources: legacyKnowledgeSources(config.knowledgeSources) }
        : {}),
    },
    triggerModes: config.triggerModes,
    autonomyLevel:
      config.autonomyLevel === 'auto_execute'
        ? 'auto_execute'
        : config.autonomyLevel === 'approval_required'
        ? 'approval_required'
        : 'read_only',
  };
}

export interface SysadminSavePayload {
  slug: string;
  displayName: string;
  description: string;
  systemPrompt: string;
  category: string;
  modelIds: string[] | undefined;
  modelId: string | null;
  toolSlugs: string[];
  functionKeys: string[];
  skillSlugs: string[];
  libraryRefs: BuilderLibraryRef[];
  workflowSlugs: string[];
  behaviorConfig: {
    autonomyLevel: 'read_only' | 'suggest' | 'auto_approve';
    maxSteps: number;
    guardrailProfile: string;
    responseFormat: 'markdown' | 'json' | 'plain';
    temperature: number;
    maxOutputTokens: number;
  };
  triggers: {
    conversational: boolean;
    scheduled: { cron: string } | null;
    event: { types: string[] } | null;
  };
}

export function serializeSysadminSavePayload(config: BuilderConfig): SysadminSavePayload {
  const slug = (config.slug || slugify(config.name)).trim();
  return {
    slug,
    displayName: config.name.trim(),
    description: config.description.trim() || config.name.trim(),
    systemPrompt: config.systemPrompt.trim(),
    category: config.category,
    modelIds: config.modelIds.length > 0 ? config.modelIds : undefined,
    modelId:
      config.modelIds.length > 0 ? config.modelIds[0] : config.modelId || null,
    toolSlugs: config.selectedTools,
    functionKeys: config.selectedFunctions,
    skillSlugs: config.selectedSkills,
    libraryRefs: knowledgeSourcesToLibraryRefs(config.knowledgeSources),
    workflowSlugs: config.selectedWorkflows,
    behaviorConfig: {
      autonomyLevel: mapAutonomyToApi(config.autonomyLevel),
      maxSteps: config.maxSteps,
      guardrailProfile: config.guardrailProfile,
      responseFormat: config.responseFormat,
      temperature: config.temperature,
      maxOutputTokens: config.maxOutputTokens,
    },
    triggers: modesToTriggers(
      config.triggerModes,
      config.cronExpression,
      config.eventTypes,
    ),
  };
}

// ─── Tenant adapter ─────────────────────────────────────────────────────────

export const tenantAdapter: BuilderAdapter = {
  scope: 'tenant',
  helperScope: 'tenant',

  useCatalog() {
    const { data: workspaceToolsResp } = useWorkspaceTools();
    const { data: realSkillsResp } = useStudioSkills({ activeOnly: true });
    const modelsQuery = useTenantArgusModels();
    // Studio R1 fix (PR #459 audit P1): the tenant adapter previously
    // called useWrapperFunctions + useCatalogWorkflows, which both hit
    // /admin/platform/* — sysadmin-only. Tenants got 401 + empty
    // Functions / Workflows pickers despite 7 published system
    // functions existing server-side. Switch to the I-M6.7-redacted
    // tenant-facing endpoints under /functions/system + /workflows/system.
    const functionsQuery = useTenantSystemFunctions();
    const workflowsQuery = useTenantSystemWorkflows();
    const libraryRefsQuery = useBuilderLibraryRefs('tenant');

    const rawTools = (workspaceToolsResp?.data ?? []) as WorkspaceToolListRow[];
    const rawSkills = (realSkillsResp?.items ?? []) as StudioSkill[];
    const rawModels = (modelsQuery.data?.items ?? []) as TenantBrand[];
    const rawFunctions = functionsQuery.data?.items ?? [];
    const rawWorkflows = workflowsQuery.data?.items ?? [];

    return {
      tools: rawTools.map((t) => ({
        id: t.id,
        slug: t.name, // tenant compose key is `name`
        displayName: t.displayName,
        policy: t.policy,
      })),
      skills: rawSkills.map((s) => ({
        id: s.id,
        slug: s.id, // tenant compose key is `id`
        displayName: s.displayName ?? s.name,
        description: s.description,
        toolDependencies: s.toolDependencies,
      })),
      functions: rawFunctions.map((f) => ({
        id: f.id,
        slug: f.functionKey,
        displayName: f.displayName,
        category: f.category,
        // Tenant-facing /functions/system surface does not expose
        // `runtime` (it's an authoring detail). Default to 'native'
        // so existing UI affordances that check runtime keep working.
        runtime: 'native',
        appliesTo: f.appliesTo,
        // System functions surfaced to tenants are always published —
        // the endpoint filters status='published' AND is_active=true.
        status: 'published' as const,
      })),
      workflows: rawWorkflows.map((w) => ({
        id: w.id,
        slug: w.slug,
        displayName: w.displayName,
        category: w.category,
        // Same as above — /workflows/system serves published only.
        status: 'published' as const,
      })),
      libraryRefs: libraryRefsQuery.data ?? [],
      libraryRefsLoading: libraryRefsQuery.isLoading,
      libraryRefsError: libraryRefsQuery.error ?? undefined,
      models: rawModels.map((m) => ({
        id: m.brandId, // tenant scope: brandId IS the compose key
        brandId: m.brandId,
        displayName: m.displayName,
        isActive: true,
      })),
      modelsLoading: modelsQuery.isLoading,
      skillsEmpty: rawSkills.length === 0,
      toolsEmpty: rawTools.length === 0,
    };
  },

  useExistingAgent(_editId) {
    // Tenant builder has no edit mode (create-only flow).
    return { existing: null, loading: false };
  },

  useSaveAgent() {
    const createAgent = useCreateAgent();
    return {
      saveInFlight: createAgent.isPending,
      save({ config, onSuccess, onError }: SaveAgentArgs) {
        const body = serializeTenantSavePayload(config);
        createAgent.mutate(body, {
          onSuccess: () => onSuccess(null),
          onError,
        });
      },
    };
  },

  validateAddTool(slug, tools) {
    return tools.some((t) => t.slug === slug);
  },
  validateAddSkill(slug, skills) {
    // Tenant skills picker stores skill.id in selectedSkills, but the
    // architect may emit by id OR by name. Pre-refactor tenant checked
    // BOTH; preserve that behavior.
    return skills.some((s) => s.id === slug || s.slug === slug || s.displayName === slug);
  },
  // No validateAddFunction — apply-mutation switch skips on tenant scope.

  transformSetIdentity(args, _currentConfig) {
    return {
      ...(args.name != null ? { name: String(args.name) } : {}),
      ...(args.description != null ? { description: String(args.description) } : {}),
      ...(args.category != null ? { category: args.category as AgentCategory } : {}),
      ...(args.icon != null ? { icon: String(args.icon) } : {}),
      ...(args.iconColor != null ? { iconColor: String(args.iconColor) } : {}),
    };
  },

  resolveSetModel(args, _models, _modelsLoading) {
    // Tenant scope writes brand_id string directly — server resolves
    // brand_id → argus_models.id via tenant_argus_model_assignments.
    const brand = typeof args.brand === 'string' ? args.brand : '';
    if (!brand) {
      return {
        patch: null,
        error: 'Architect emitted set_model without a brand value',
        loading: false,
      };
    }
    return {
      patch: { modelId: brand, modelIds: [brand] },
      error: null,
      loading: false,
    };
  },

  showLifecycleBar: false,
  showFunctionsPicker: 'disabled-preview',
  showWorkflowsPicker: 'disabled-preview',
  totalSections: 8,
  sectionIndices: [1, 2, 3, 4, 5, 6, 7, 8],
  backRoute: '/agents/new',
  subtitle(_isEditing) {
    return 'AI-assisted agent creation';
  },
  getSectionStatus(config, section) {
    switch (section) {
      case 1: return config.name ? (config.description ? 'complete' : 'partial') : 'empty';
      case 2: return config.systemPrompt ? (config.systemPrompt.length > 100 ? 'complete' : 'partial') : 'empty';
      case 3: return config.modelId ? 'complete' : 'empty';
      case 4: return config.selectedTools.length > 0 ? 'complete' : 'empty';
      case 5: return config.selectedSkills.length > 0 ? 'complete' : 'empty';
      case 6: return config.guardrailProfile ? 'complete' : 'empty';
      case 7: return config.triggerModes.length > 0 ? 'complete' : 'empty';
      case 8: return 'complete';
      default: return 'empty';
    }
  },

  canSave(config, _isEditing) {
    return !!(config.name.trim() && config.systemPrompt.trim() && config.selectedTools.length > 0);
  },

  showCatalogPickerForTools: true,
  showCatalogPickerForModels: true,
  showSlugField: false,
};

// ─── Sysadmin adapter ───────────────────────────────────────────────────────

export const sysadminAdapter: BuilderAdapter = {
  scope: 'sysadmin',
  helperScope: 'sysadmin',

  useCatalog() {
    const modelsQuery = useArgusModels();
    const { data: sysadminToolsResp } = useSysadminTools();
    const skillsQuery = useCatalogSkills();
    const workflowsQuery = useCatalogWorkflows();
    const { data: functionsResp } = useWrapperFunctions({ status: 'published' });
    const libraryRefsQuery = useBuilderLibraryRefs('sysadmin');

    const rawModels = (modelsQuery.data?.items ?? []) as ArgusModelSummary[];
    const rawTools = (sysadminToolsResp?.items ?? []) as SysadminToolListItem[];
    const rawSkills = (skillsQuery.data?.pages?.flatMap((p) => p.items) ?? []) as CatalogSkill[];
    const rawWorkflows = (workflowsQuery.data?.pages?.flatMap((p) => p.items) ?? []) as CatalogWorkflow[];
    const rawFunctions = (functionsResp?.items ?? []) as WrapperFunction[];

    return {
      tools: rawTools.map((t) => ({
        id: t.id,
        slug: t.slug,
        displayName: t.displayName,
        policy: t.policy,
      })),
      skills: rawSkills.map((s) => ({
        id: s.id,
        slug: s.slug,
        displayName: s.displayName,
        description: s.description,
        toolDependencies: s.toolDependencies,
      })),
      functions: rawFunctions.map((f) => ({
        id: f.id,
        slug: f.functionKey,
        displayName: f.displayName,
        category: f.category,
        runtime: f.runtime,
        appliesTo: f.appliesTo,
        status: f.status,
      })),
      workflows: rawWorkflows.map((w) => ({
        id: w.id,
        slug: w.slug,
        displayName: w.displayName,
        category: w.category,
        status: w.status,
      })),
      libraryRefs: libraryRefsQuery.data ?? [],
      libraryRefsLoading: libraryRefsQuery.isLoading,
      libraryRefsError: libraryRefsQuery.error ?? undefined,
      models: rawModels.map((m) => ({
        id: m.id,
        brandId: m.brandId,
        displayName: m.displayName,
        isActive: m.isActive,
      })),
      modelsLoading: modelsQuery.isLoading,
      skillsEmpty: rawSkills.length === 0,
      toolsEmpty: rawTools.length === 0,
    };
  },

  useExistingAgent(editId) {
    const { data: existingResp, isLoading } = useCatalogAgent(editId);
    const existing: CatalogAgentDetail | undefined = existingResp?.data;

    // fix/argus-live-publish-cluster defect #4 — React error #185
    // ("Maximum update depth exceeded") on /sysadmin/catalog/agents/<id>.
    //
    // The previous implementation returned a freshly-constructed
    // `normalized` object literal on EVERY render. `<AgentBuilder>`
    // consumes `existing` in a `useEffect([existing])` that calls
    // `setConfig` + `setOpenSections` + `setPhase`. Because the reference
    // changed each render even when the underlying React Query data was
    // stable, the effect re-fired on every render, triggering a state
    // update that triggered another render — infinite loop, ErrorBoundary
    // catches React 185, the operator sees "Something went wrong" and
    // can never view or edit a system agent. PR #543 audit P0.
    //
    // Memoizing on the React-Query-stable `existing` reference makes the
    // returned object stable until the actual data changes. `useMemo`
    // is unconditional so hook order is preserved when `existing` is
    // null on the first render (loading state).
    const normalized = useMemo<NormalizedExistingAgent | null>(() => {
      if (!existing) return null;
      return {
        slug: existing.slug,
        displayName: existing.displayName,
        description: existing.description,
        systemPrompt: existing.systemPrompt,
        category: existing.category,
        modelId: existing.modelId,
        modelIds:
          existing.modelIds ?? (existing.modelId ? [existing.modelId] : []),
        toolSlugs: existing.toolSlugs,
        functionKeys: existing.functionKeys,
        skillSlugs: existing.skillSlugs,
        libraryRefs: existing.libraryRefs ?? [],
        workflowSlugs: existing.workflowSlugs,
        triggers: existing.triggers,
        behaviorConfig: existing.behaviorConfig,
        raw: existing,
      };
    }, [existing]);

    if (!normalized) {
      return { existing: null, loading: isLoading };
    }
    return { existing: normalized, loading: false };
  },

  useSaveAgent() {
    const createAgent = useCreateCatalogAgent();
    const updateAgent = useUpdateCatalogAgent();
    return {
      saveInFlight: createAgent.isPending || updateAgent.isPending,
      save({ config, isEditing, editId, onSuccess, onError }: SaveAgentArgs) {
        const body = serializeSysadminSavePayload(config);
        if (isEditing && editId) {
          updateAgent.mutate(
            { id: editId, ...body },
            {
              onSuccess: () => onSuccess(editId),
              onError,
            },
          );
        } else {
          createAgent.mutate(body, {
            onSuccess: (resp) => onSuccess(resp.data.id),
            onError,
          });
        }
      },
    };
  },

  validateAddTool(slug, tools) {
    return tools.some((t) => t.slug === slug);
  },
  validateAddSkill(slug, skills) {
    return skills.some((s) => s.slug === slug);
  },
  validateAddFunction(slug, functions) {
    if (!functions) return false;
    return functions.some((f) => f.slug === slug);
  },

  transformSetIdentity(args, currentConfig) {
    return {
      ...(args.name != null
        ? { name: String(args.name), slug: currentConfig.slug || slugify(String(args.name)) }
        : {}),
      ...(args.description != null ? { description: String(args.description) } : {}),
      ...(args.category != null ? { category: args.category as AgentCategory } : {}),
      ...(args.icon != null ? { icon: String(args.icon) } : {}),
      ...(args.iconColor != null ? { iconColor: String(args.iconColor) } : {}),
    };
  },

  resolveSetModel(args, models, modelsLoading) {
    // Sysadmin scope: brand → UUID via applySetModelMutation. The pure
    // resolver returns { patch, error, loading }; the caller surfaces
    // the toast(s).
    return applySetModelMutation(models, args, { isLoading: modelsLoading });
  },

  showLifecycleBar: true,
  showFunctionsPicker: 'enabled',
  showWorkflowsPicker: 'enabled',
  totalSections: 10,
  sectionIndices: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  backRoute: '/sysadmin/catalog/agents',
  subtitle(isEditing) {
    return isEditing ? 'Editing system agent — sysadmin scope' : 'AI-assisted system agent creation';
  },
  getSectionStatus(config, section) {
    switch (section) {
      case 1: return config.name ? (config.description ? 'complete' : 'partial') : 'empty';
      case 2: return config.systemPrompt ? (config.systemPrompt.length > 100 ? 'complete' : 'partial') : 'empty';
      case 3: return config.modelId ? 'complete' : 'empty';
      case 4: return config.selectedTools.length > 0 ? 'complete' : 'empty';
      case 5: return config.selectedFunctions.length > 0 ? 'complete' : 'empty';
      case 6: return config.selectedSkills.length > 0 ? 'complete' : 'empty';
      case 7: return config.guardrailProfile ? 'complete' : 'empty';
      case 8: return config.selectedWorkflows.length > 0 ? 'partial' : 'empty';
      case 9: return config.triggerModes.length > 0 ? 'complete' : 'empty';
      case 10: return 'complete';
      default: return 'empty';
    }
  },

  canSave(config, isEditing) {
    // Sysadmin canSave intentionally OMITS the `selectedTools.length > 0`
    // gate — system agents like system.ask_argus or system.prompt_helper
    // may legitimately have zero tools.
    return !!(
      config.name.trim() &&
      config.systemPrompt.trim() &&
      (isEditing || config.slug.trim() || slugify(config.name).length > 0)
    );
  },

  showCatalogPickerForTools: false,
  showCatalogPickerForModels: false,
  showSlugField: true,
};

// ─── Adapter resolver ───────────────────────────────────────────────────────

export function getAdapter(scope: 'tenant' | 'sysadmin'): BuilderAdapter {
  return scope === 'sysadmin' ? sysadminAdapter : tenantAdapter;
}

// ─── Helpers shared with the seeding effect ────────────────────────────────

export function seedConfigFromExisting(
  existing: NormalizedExistingAgent,
  prev: BuilderConfig,
): BuilderConfig {
  return {
    ...prev,
    slug: existing.slug,
    name: existing.displayName,
    description: existing.description,
    systemPrompt: existing.systemPrompt,
    modelId: existing.modelId ?? '',
    modelIds: existing.modelIds ?? (existing.modelId ? [existing.modelId] : []),
    category: (existing.category as AgentCategory) || prev.category,
    selectedTools: existing.toolSlugs,
    selectedFunctions: existing.functionKeys,
    selectedSkills: existing.skillSlugs,
    knowledgeSources: libraryRefsToPickerValues(existing.libraryRefs),
    selectedWorkflows: existing.workflowSlugs,
    triggerModes: triggersToModes(existing.triggers) as AgentTrigger[],
    cronExpression: existing.triggers.scheduled?.cron ?? '',
    eventTypes: existing.triggers.event?.types ?? [],
    autonomyLevel: mapAutonomyFromApi(existing.behaviorConfig.autonomyLevel),
    guardrailProfile:
      (existing.behaviorConfig.guardrailProfile as BuilderConfig['guardrailProfile']) ||
      'standard',
    maxSteps: existing.behaviorConfig.maxSteps ?? prev.maxSteps,
  };
}

// Suppress unused-warning when only types are imported in some build paths.
void toast;
